import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Welcome() {
  const navigate = useNavigate();

  // Brand guard: if no brand in LS, go to step-1
  const brand = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("onboarding.brand") || "{}"); }
    catch { return {}; }
  }, []);
  useEffect(() => {
    if (!brand?.businessName) navigate("/onboarding/step-1", { replace: true });
  }, [brand, navigate]);

  // Form state (mirrors your Generate.jsx settings)
  const [prompt, setPrompt] = useState("");
  const [contentType, setContentType] = useState("informative");
  const [tone, setTone] = useState("friendly");
  const [length, setLength] = useState("medium");

  function handleGenerate() {
    // Persist the settings so Generate.jsx picks them up
    localStorage.setItem("onboarding.settings", JSON.stringify({ contentType, tone, length }));
    // You can also persist the prompt as a title seed if you want
    if (prompt.trim()) localStorage.setItem("welcome.prompt", JSON.stringify({ prompt }));
    navigate("/generate");
  }

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#f0f4ff,#ffffff)" }}>
      {/* Top bar with avatar + logout (also reused below in step 4) */}
      <header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 20px", borderBottom: "1px solid #eef1f5", background: "#fff"
      }}>
        <div style={{ fontWeight: 700, fontSize: 20 }}>Clikkle Creator</div>
        <UserMenu />
      </header>

      {/* Hero */}
      <section style={{ maxWidth: 980, margin: "40px auto 16px", padding: "0 20px" }}>
        <h1 style={{ fontSize: 36, margin: "0 0 12px" }}>
          Create standout content in minutes
        </h1>
        <p style={{ color: "#6b7280", marginBottom: 24 }}>
          Describe what you want, pick style options, and we’ll generate a polished draft.
        </p>

        {/* “Create New” — Promo-like panel */}
        <div style={{
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16,
          padding: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}>
          <div style={{ marginBottom: 12, fontWeight: 600 }}>Describe your content</div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={`e.g., “Blog post about winter tree care tips in Amarillo”`}
            style={{
              width: "100%", minHeight: 96, padding: 12, borderRadius: 12,
              border: "1px solid #e5e7eb", outline: "none", resize: "vertical"
            }}
          />
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12
          }}>
            <select value={contentType} onChange={(e) => setContentType(e.target.value)}
              style={selectStyle}>
              <option value="informative">Content type: Informative</option>
              <option value="promotional">Content type: Promotional</option>
              <option value="creative">Content type: Creative</option>
            </select>
            <select value={tone} onChange={(e) => setTone(e.target.value)}
              style={selectStyle}>
              <option value="friendly">Tone: Friendly</option>
              <option value="formal">Tone: Formal</option>
              <option value="persuasive">Tone: Persuasive</option>
            </select>
            <select value={length} onChange={(e) => setLength(e.target.value)}
              style={selectStyle}>
              <option value="short">Length: Short</option>
              <option value="medium">Length: Medium</option>
              <option value="long">Length: Long</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <button onClick={handleGenerate} style={primaryBtn}>Generate</button>
            <button onClick={() => navigate("/drafts")} style={ghostBtn}>
              Open Drafts
            </button>
            <button onClick={() => navigate("/dashboard")} style={ghostBtn}>
              My Calendar
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ——— Simple styles ——— */
const selectStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid #e5e7eb",
  background: "#fff"
};
const primaryBtn = {
  padding: "10px 18px", borderRadius: 12, border: "1px solid #3b82f6",
  background: "#3b82f6", color: "#fff", fontWeight: 600
};
const ghostBtn = {
  padding: "10px 18px", borderRadius: 12, border: "1px solid #e5e7eb",
  background: "#fff", color: "#111827", fontWeight: 600
};

/* ——— Lightweight user menu + logout ——— */
function UserMenu() {
  const navigate = useNavigate();
  function logout() {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("user");
      // keep brand/settings if you want, otherwise also clear them:
      // localStorage.removeItem("onboarding.brand");
      // localStorage.removeItem("onboarding.settings");
    } finally {
      navigate("/login", { replace: true });
    }
  }
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  })();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6",
        display: "grid", placeItems: "center", fontSize: 14, fontWeight: 700
      }}>
        {(user?.name || "U").slice(0,1).toUpperCase()}
      </div>
      <button onClick={logout}
        style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "6px 10px", background: "#fff" }}>
        Logout
      </button>
    </div>
  );
}
